import React, { useState } from 'react';
import { earningsWorkbook, deductionsWorkbook, employer } from '../data/mockData';

const earningsTotal = earningsWorkbook.filter(r => r.status !== 'CRITICAL').reduce((s, r) => s + r.amount, 0);
const earningsTotalAll = earningsWorkbook.reduce((s, r) => s + r.amount, 0);

export default function WorkbookOutput() {
  const [tab, setTab] = useState('earnings');

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>Workbook Output</h2>
          <p>ACM-2024-001_PrimePay_Enhanced_Workbook_v8_POPULATED.xlsx · Extractor V3 Skill output</p>
        </div>
        <span className="badge badge-success" style={{ padding: '6px 14px' }}>✓ Workbook Generated</span>
      </div>

      {/* Batch Control */}
      <div className="card section-gap">
        <div className="card-header"><h2>Batch Control</h2></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[
              ['Employer Unique ID', employer.employerUniqueId],
              ['Pay Period Start', employer.payPeriodStart],
              ['Pay Period End', employer.payPeriodEnd],
              ['Processing Mode', employer.processingMode],
              ['Pipeline Run ID', employer.lastRunId],
              ['Earnings Rows', earningsWorkbook.length],
              ['Deduction Rows', deductionsWorkbook.length],
              ['Source Files', '3 (EML, CSV, XLSX)'],
            ].map(([label, value]) => (
              <div key={label} style={{ background: 'var(--surface-2)', padding: '12px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 10, color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', fontFamily: typeof value === 'string' && (value.includes('-') || value.includes('EMP')) ? 'var(--font-mono)' : undefined, fontSize: 12 }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="tab-list">
        <button className={`tab-btn ${tab === 'earnings' ? 'active' : ''}`} onClick={() => setTab('earnings')}>
          Earnings Sheet ({earningsWorkbook.length} rows)
        </button>
        <button className={`tab-btn ${tab === 'deductions' ? 'active' : ''}`} onClick={() => setTab('deductions')}>
          Deductions Upsert ({deductionsWorkbook.length} rows)
        </button>
      </div>

      {tab === 'earnings' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div className="card" style={{ flex: 1, padding: '14px 18px' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Loadable Gross Total</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--success)' }}>
                ${earningsTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div className="card" style={{ flex: 1, padding: '14px 18px' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Source Document Total</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--ink)' }}>$17,012.00</div>
            </div>
            <div className="card" style={{ flex: 1, padding: '14px 18px', border: '1px solid #f5c6c6' }}>
              <div style={{ fontSize: 11, color: 'var(--error)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Variance (CRITICAL)</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--error)' }}>−$56.25</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2>Earnings Sheet</h2>
              <span className="card-count">{earningsWorkbook.length} rows</span>
            </div>
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Employee GUID</th>
                    <th>Code</th>
                    <th>Description</th>
                    <th>Calc</th>
                    <th className="num">Hours</th>
                    <th className="num">Rate</th>
                    <th className="num">Multiplier</th>
                    <th className="num">Amount</th>
                    <th>RowStamp</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {earningsWorkbook.map((row, i) => {
                    const isCrit = row.status === 'CRITICAL';
                    return (
                      <tr key={i} style={{ background: isCrit ? '#fff8f8' : undefined }}>
                        <td style={{ fontWeight: 600 }}>{row.name}</td>
                        <td>
                          <span className={`mono`} style={{
                            fontSize: 11,
                            color: isCrit ? 'var(--error)' : 'var(--ink-4)',
                            fontFamily: 'var(--font-mono)'
                          }}>
                            {row.employeeUniqueId}
                          </span>
                        </td>
                        <td><span className="badge badge-primary mono">{row.earningCode}</span></td>
                        <td style={{ fontSize: 12 }}>{row.description}</td>
                        <td><span className="badge badge-neutral mono">{row.calcMethod}</span></td>
                        <td className="num mono">{row.hours > 0 ? row.hours : '—'}</td>
                        <td className="num mono">{row.rate > 0 ? `$${row.rate.toFixed(2)}` : '—'}</td>
                        <td className="num mono">{row.multiplier !== 1.0 ? `×${row.multiplier}` : '—'}</td>
                        <td className="num mono" style={{ fontWeight: 700, color: isCrit ? 'var(--error)' : 'var(--ink)' }}>
                          ${row.amount.toFixed(2)}
                        </td>
                        <td>
                          <span className="mono" style={{ fontSize: 10, color: isCrit ? 'var(--error)' : 'var(--ink-4)' }}>
                            {row.rowStamp}
                          </span>
                        </td>
                        <td>
                          <span className={`badge badge-${isCrit ? 'critical' : 'success'}`}>
                            {isCrit ? 'CRITICAL' : 'OK'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'deductions' && (
        <div className="card">
          <div className="card-header">
            <h2>Deductions Upsert Sheet</h2>
            <span className="card-count">{deductionsWorkbook.length} rows</span>
          </div>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Deduction Code</th>
                  <th>Description</th>
                  <th>Calc Method</th>
                  <th className="num">Rate</th>
                  <th className="num">Amount</th>
                  <th>RowStamp</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {deductionsWorkbook.map((row, i) => {
                  const isHigh = row.status === 'HIGH';
                  return (
                    <tr key={i} style={{ background: isHigh ? '#fffbf5' : undefined }}>
                      <td style={{ fontWeight: 600 }}>{row.name}</td>
                      <td><span className="badge badge-neutral mono">{row.deductionCode}</span></td>
                      <td style={{ fontSize: 12 }}>{row.description}</td>
                      <td><span className="badge badge-neutral mono">{row.calcMethod}</span></td>
                      <td className="num mono">{row.rate > 0 ? `${(row.rate * 100).toFixed(0)}%` : '—'}</td>
                      <td className="num mono" style={{ fontWeight: 700 }}>${row.amount.toFixed(2)}</td>
                      <td><span className="mono" style={{ fontSize: 10, color: 'var(--ink-4)' }}>{row.rowStamp}</span></td>
                      <td>
                        <span className={`badge badge-${isHigh ? 'high' : 'success'}`}>
                          {isHigh ? 'HIGH' : 'OK'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
